// Music-page sort module: type, options, and sort function. Shuffle is
// modeled as a directionless option whose ordering is owned by the caller
// (Music walks its pre-shuffled index permutation directly).

import type {
  Album,
  Artist,
  Song,
} from '@airhead-games/shared/types/configs/media/musicLibrary';
import type { SortOption } from '../../components/collectionToolbar';
import { applySortDirection, type SortDirection } from '../../utilities/sort';

export type SongEntry = {
  song: Song;
  album: Album;
  artist: Artist;
};

export const MUSIC_SORT_FIELDS = [
  'shuffle',
  'title',
  'artist',
  'album',
  'duration',
] as const;
export type MusicSortField = (typeof MUSIC_SORT_FIELDS)[number];

export const DEFAULT_MUSIC_SORT_FIELD: MusicSortField = 'shuffle';
export const DEFAULT_MUSIC_SORT_DIRECTION: SortDirection = 'asc';

export const MUSIC_SORT_OPTIONS: SortOption<MusicSortField>[] = [
  { value: 'shuffle', label: 'Shuffle' },
  {
    value: 'title',
    label: 'Title',
    directionLabels: { asc: 'A > Z', desc: 'Z > A' },
  },
  {
    value: 'artist',
    label: 'Artist',
    directionLabels: { asc: 'A > Z', desc: 'Z > A' },
  },
  {
    value: 'album',
    label: 'Album',
    directionLabels: { asc: 'A > Z', desc: 'Z > A' },
  },
  {
    value: 'duration',
    label: 'Duration',
    directionLabels: { asc: 'Shortest', desc: 'Longest' },
  },
];

export const sortMusicSongs = (
  entries: SongEntry[],
  field: MusicSortField,
  direction: SortDirection
): SongEntry[] => {
  if (field === 'shuffle') {
    return entries;
  }

  return [...entries].sort((a, b) => {
    if (field === 'title') {
      const primary = a.song.title.localeCompare(b.song.title);
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
    }

    if (field === 'artist') {
      const primary = a.artist.name.localeCompare(b.artist.name);
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
      const albumTiebreak = a.album.name.localeCompare(b.album.name);
      if (albumTiebreak !== 0) {
        return albumTiebreak;
      }
    }

    if (field === 'album') {
      const primary = a.album.name.localeCompare(b.album.name);
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
      const trackA = a.song.albumTrackNumber ?? Number.POSITIVE_INFINITY;
      const trackB = b.song.albumTrackNumber ?? Number.POSITIVE_INFINITY;
      if (trackA !== trackB) {
        return trackA - trackB;
      }
    }

    if (field === 'duration') {
      const primary = a.song.duration - b.song.duration;
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
    }

    return a.song.title.localeCompare(b.song.title);
  });
};
